// The state machine is intentionally tiny. It knows nothing about specific
// phases — phases are passed in. Phase contract: docs/fe-state-machine.md.

import type { Context, Phase, PhaseName, TransitionFn } from "./types";

type MachineOptions = {
  root: HTMLElement;
  context: Context;
  phases: Record<PhaseName, Phase>;
};

export type Machine = {
  start(initial: PhaseName): void;
  current(): PhaseName | null;
};

export function createMachine(opts: MachineOptions): Machine {
  let current: PhaseName | null = null;
  let rafId: number | null = null;
  let lastTs = 0;

  const transition: TransitionFn = (next, payload) => {
    void doTransition(next, payload);
  };

  async function doTransition(next: PhaseName, payload?: Partial<Context>) {
    if (current) {
      const prev = opts.phases[current];
      try {
        await prev.exit?.(opts.context);
      } catch (err) {
        console.error(`[state] exit(${current}) threw`, err);
      }
    }
    if (payload) Object.assign(opts.context, payload);

    current = next;
    const phase = opts.phases[next];

    stopLoop();
    if (phase.render) startLoop();

    try {
      await phase.enter(opts.context, transition);
    } catch (err) {
      console.error(`[state] enter(${next}) threw`, err);
    }
  }

  function startLoop() {
    lastTs = performance.now();
    const tick = (ts: number) => {
      const dt = (ts - lastTs) / 1000;
      lastTs = ts;
      if (current) {
        const phase = opts.phases[current];
        try {
          phase.render?.(dt, opts.context);
        } catch (err) {
          console.error(`[state] render(${current}) threw`, err);
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
  }

  function stopLoop() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  return {
    start(initial) {
      void doTransition(initial);
    },
    current: () => current,
  };
}
